import { Injectable, inject } from '@angular/core';
import { StorageService } from '../storage/storage.service';
import { StorageType } from '../storage/storage.type';
import { IToken } from '../../interfaces';
import { map, Observable, switchMap, take, catchError, throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { HttpResponse, QueryParams } from './base-http.type';
import { UtilsService } from '../utils.service';
import { errorNotificationMessages } from '../../constants';
import { RequestNesting } from '../../enums';

const AUTH_HEADER = 'x-auth-token';

const DEFAULT_HEADERS: Record<string, string> = {
  Accept: 'application/json, text/plain, */*',
  'X-Requested-With': 'XMLHttpRequest',
  'Cache-Control': 'no-cache',
  Pragma: 'no-cache',
  'Sec-Fetch-Site': 'same-origin',
  'Content-Type': 'application/json',
};

@Injectable({
  providedIn: 'root',
})
export class BaseHttpService {
  private _storageService = inject(StorageService);
  private _utils = inject(UtilsService);
  private _http = inject(HttpClient);

  private _baseHref = '/api/v1';

  private _createDefaultHeaders(
    headersToSet?: Record<string, string>,
  ): Observable<Record<string, string>> {
    const headers = headersToSet ? { ...headersToSet } : { ...DEFAULT_HEADERS };

    return this._storageService.getItem<IToken>(StorageType.Token).pipe(
      map((token: IToken) => {
        if (token?.access_token) {
          return { ...headers, [AUTH_HEADER]: token.access_token };
        }
        return headers;
      }),
    );
  }

  public createHeaders(headers: Record<string, string>) {
    return this._createDefaultHeaders(headers);
  }

  public getImage(url: string): Observable<Blob> {
    const HEADERS = { 'Cache-Control': 'no-cache' };
    return this.createHeaders(HEADERS).pipe(
      take(1),
      switchMap((headers) => {
        const { [AUTH_HEADER]: _authHeader, ...rest } = headers;
        return this._http.get(this._baseHref + url, {
          responseType: 'blob',
          headers: rest,
        });
      }),
    );
  }

  public getFile(url: string): Observable<ArrayBuffer> {
    const headers = {
      Accept: 'application/pdf, application/octet-stream, */*',
      'Cache-Control': 'no-cache',
    };

    return this.createHeaders(headers).pipe(
      take(1),
      switchMap((authHeaders) => {
        const { 'Content-Type': _contentType, ...rest } = authHeaders;

        return this._http.get(url, {
          responseType: 'arraybuffer',
          headers: new HttpHeaders(rest),
        });
      }),
    );
  }

  public get<R>(
    url: string,
    params?: QueryParams,
    type?: RequestNesting,
  ): Observable<HttpResponse<R>['data']> {
    return this._createDefaultHeaders().pipe(
      take(1),
      switchMap((headers) => {
        const httpParams = this._toHttpParams(this._removeNullParams(params));
        return this._http
          .get<HttpResponse<R>>(this._baseHref + url, {
            params: httpParams ?? undefined,
            headers: new HttpHeaders(headers),
            observe: 'response',
          })
          .pipe(
            map((response) => this._handleResponse(response, type)),
            catchError((e) => this._handleErrorAndRethrow(e)),
          );
      }),
    );
  }

  public post<R>(
    url: string,
    data?: QueryParams,
    type?: RequestNesting,
  ): Observable<HttpResponse<R>['data']> {
    return this._createDefaultHeaders().pipe(
      take(1),
      switchMap((headers) => {
        return this._http
          .post<HttpResponse<R>>(this._baseHref + url, data ?? {}, {
            headers: new HttpHeaders(headers),
            observe: 'response',
          })
          .pipe(
            map((response) => this._handleResponse(response, type)),
            catchError((e) => this._handleErrorAndRethrow(e)),
          );
      }),
    );
  }

  public put<R>(
    url: string,
    data?: QueryParams,
    type?: RequestNesting,
  ): Observable<HttpResponse<R>['data']> {
    return this._createDefaultHeaders().pipe(
      take(1),
      switchMap((headers) => {
        return this._http
          .put<HttpResponse<R>>(this._baseHref + url, data ?? {}, {
            headers: new HttpHeaders(headers),
            observe: 'response',
          })
          .pipe(
            map((response) => this._handleResponse(response, type)),
            catchError((e) => this._handleErrorAndRethrow(e)),
          );
      }),
    );
  }

  public postFormData<R>(url: string, data: FormData): Observable<HttpResponse<R>['data']> {
    return this.createHeaders({}).pipe(
      take(1),
      switchMap((headers) =>
        this._http
          .post<HttpResponse<R>>(`${this._baseHref}${this._baseHref}${url}`, data, {
            headers: new HttpHeaders(headers),
            observe: 'response',
          })
          .pipe(
            map((response) => this._handleResponse(response, RequestNesting.withoutData)),
            catchError((e) => this._handleErrorAndRethrow(e)),
          ),
      ),
    );
  }

  public delete<R>(url: string, type?: RequestNesting): Observable<HttpResponse<R>['data']> {
    return this._createDefaultHeaders().pipe(
      take(1),
      switchMap((headers) => {
        return this._http
          .delete<HttpResponse<R>>(this._baseHref + url, {
            headers: new HttpHeaders(headers),
            observe: 'response',
          })
          .pipe(
            map((response) => this._handleResponse(response, type)),
            catchError((e) => this._handleErrorAndRethrow(e)),
          );
      }),
    );
  }

  private _handleResponse<R>(
    response: { body: HttpResponse<R> | null; headers: HttpHeaders },
    type?: RequestNesting,
  ): HttpResponse<R>['data'] {
    const authHeaderValue = response.headers.get(AUTH_HEADER);
    if (authHeaderValue) {
      this._storageService.setItem(StorageType.Token, { access_token: authHeaderValue });
    }
    const data = response.body;
    const bodyWithError = data as (HttpResponse<R> & { error?: unknown }) | null;
    if (bodyWithError?.error) {
      throw new HttpErrorResponse({ error: bodyWithError });
    }
    return (
      type === RequestNesting.withoutData
        ? (data as HttpResponse<R>)
        : (data as HttpResponse<R>).data
    ) as HttpResponse<R>['data'];
  }

  private _handleErrorAndRethrow(e: HttpErrorResponse): Observable<never> {
    this._handleError(e);
    return throwError(() => e);
  }

  private _handleError(e: HttpErrorResponse): void {
    const code = e.status;
    const responseErrorCode = this._getErrorCode(e);

    if (code === 403) {
      this._storageService.clearStorage();
    } else if (
      !this._getErrorDetails(e)?.includes('No posts pinned') &&
      responseErrorCode !== 401
    ) {
      this._utils.openNotification(this._getErrorMessage(e), true);
    }
  }

  private _getErrorCode(e: HttpErrorResponse): number | undefined {
    return e.error?.error?.code ?? e.error?.code;
  }

  private _getErrorDetails(e: HttpErrorResponse): string | undefined {
    return e.error?.error?.details ?? e.error?.details;
  }

  private _getErrorMessage(e: HttpErrorResponse): string {
    const responseError = e.error?.error;
    const errorMessage =
      typeof responseError?.message === 'string' ? responseError.message.trim() : '';
    const message = typeof e.error?.message === 'string' ? e.error.message.trim() : '';

    return errorMessage || message || errorNotificationMessages.default;
  }

  private _toHttpParams(params: QueryParams | null): HttpParams | null {
    if (!params || Object.keys(params).length === 0) return null;
    return new HttpParams({ fromObject: params as Record<string, string> });
  }

  private _removeNullParams(params: QueryParams | undefined): QueryParams | null {
    if (!params) return null;
    return Object.entries(params).reduce(
      (acc: QueryParams, [k, v]) => (v == null || v === '' ? acc : { ...acc, [k]: v.toString() }),
      {},
    );
  }
}
